// lib/AIChat.js - Simplified version (validation handled by main AI function)
import { appwriteConfig } from './appwrite'
import { Client, Functions } from 'appwrite'

const client = new Client()
client
  .setEndpoint(appwriteConfig.endpoint)
  .setProject(appwriteConfig.projectId)

const functions = new Functions(client)

/**
 * Query AI with company-specific dynamic settings
 * All validation and escalation logic is handled by the main AI function
 */
export const queryAI = async (question, companyId, conversationId, previousMessages = []) => {
  try {
    console.log('🤖 Querying AI:', question)
    console.log('🏢 Company ID:', companyId)

    const execution = await functions.createExecution(
      '6838da77003ae8a07cb6', // Your enhanced AI function ID
      JSON.stringify({
        question,
        companyId,
        conversationId,
        previousMessages: previousMessages.slice(-6) // Last 6 messages for context
      }),
      false
    )

    if (!execution.responseBody) {
      throw new Error('No response from AI function')
    }

    const response = JSON.parse(execution.responseBody)
    
    if (!response.success) {
      throw new Error(response.error || 'AI query failed')
    }

    // ✅ The main AI function now handles all validation and uses dynamic thresholds
    // So we can directly return the response
    console.log('✅ AI response received:', {
      answer: response.data.answer?.substring(0, 80) + '...',
      confidence: response.data.confidence,
      escalated: response.data.requiresHumanAgent,
      usedKnowledge: response.data.usedDocuments || response.data.usedFAQs,
      analytics: response.data.analytics
    })

    return response.data

  } catch (error) {
    console.error('❌ Error querying AI:', error)
    
    // Return safe fallback response
    return {
      answer: "I'm having technical difficulties right now. Let me connect you with a human agent who can help you immediately! 😊",
      confidence: 'low',
      requiresHumanAgent: true,
      escalationReason: 'technical_error',
      sources: [],
      analytics: {
        responseTime: 0,
        errorOccurred: true
      }
    }
  }
}

/**
 * ✅ NEW: Get company AI preferences (for debugging/testing)
 * This can be used to show current settings in the chat widget if needed
 */
export const getCompanyAIPreferences = async (companyId) => {
  try {
    console.log('🎨 Fetching AI preferences for company:', companyId)
    
    const execution = await functions.createExecution(
      '6838da77003ae8a07cb6', // Same AI function
      JSON.stringify({
        action: 'get_preferences',
        companyId: companyId
      }),
      false
    )

    if (!execution.responseBody) {
      return null
    }

    const response = JSON.parse(execution.responseBody)
    
    if (response.success && response.preferences) {
      console.log('✅ AI preferences loaded:', response.preferences)
      return response.preferences
    }

    return null

  } catch (error) {
    console.error('❌ Error fetching AI preferences:', error)
    return null
  }
}

/**
 * ✅ NEW: Test AI response with different confidence thresholds
 * This can be used for A/B testing different settings
 */
export const testAIWithThreshold = async (question, companyId, testThreshold) => {
  try {
    console.log(`🧪 Testing AI with threshold: ${testThreshold}%`)
    
    const execution = await functions.createExecution(
      '6838da77003ae8a07cb6',
      JSON.stringify({
        question,
        companyId,
        testMode: true,
        testThreshold: testThreshold
      }),
      false
    )

    if (!execution.responseBody) {
      throw new Error('No response from AI function')
    }

    const response = JSON.parse(execution.responseBody)
    
    console.log(`✅ Test result with ${testThreshold}% threshold:`, {
      escalated: response.data?.requiresHumanAgent,
      confidence: response.data?.confidence,
      reason: response.data?.escalationReason
    })

    return response.data

  } catch (error) {
    console.error('❌ Error testing AI threshold:', error)
    throw error
  }
}

/**
 * ✅ NEW: Submit feedback for AI response
 * This helps improve the AI over time
 */
export const submitAIFeedback = async (conversationId, feedback) => {
  try {
    console.log('📝 Submitting AI feedback:', feedback)
    
    const execution = await functions.createExecution(
      '6838da77003ae8a07cb6',
      JSON.stringify({
        action: 'submit_feedback',
        conversationId: conversationId,
        feedback: feedback
      }),
      false
    )

    if (!execution.responseBody) {
      throw new Error('No response from AI function')
    }

    const response = JSON.parse(execution.responseBody)
    
    if (response.success) {
      console.log('✅ Feedback submitted successfully')
      return true
    }

    return false

  } catch (error) {
    console.error('❌ Error submitting feedback:', error)
    return false
  }
}

/**
 * ✅ Utility: Check if response indicates escalation
 */
export const shouldEscalate = (aiResponse) => {
  return aiResponse?.requiresHumanAgent === true
}

/**
 * ✅ Utility: Get escalation reason in human-readable format
 */
export const getEscalationReason = (aiResponse) => {
  const reason = aiResponse?.escalationReason || 'unknown'
  
  const reasonMap = {
    'pricing_question_auto_escalation': 'Pricing question - connecting to sales',
    'no_relevant_knowledge_sources': 'No specific information available',
    'low_confidence_validation': 'Want to ensure accuracy',
    'pricing_hallucination_prevented': 'Need accurate pricing info',
    'confidence_too_low': 'Want to provide best assistance',
    'technical_error': 'Technical issue occurred',
    'customer_request': 'Customer requested human agent'
  }
  
  return reasonMap[reason] || 'Connecting you with an expert'
}

/**
 * ✅ Utility: Format AI analytics for display
 */
export const formatAIAnalytics = (analytics) => {
  if (!analytics) return null
  
  return {
    responseTime: `${analytics.responseTime || 0}ms`,
    confidence: analytics.confidence || 'unknown',
    sourcesUsed: `${(analytics.documentsUsed || 0) + (analytics.faqsUsed || 0)} sources`,
    escalationRate: analytics.escalationRate ? `${analytics.escalationRate}%` : 'N/A',
    questionType: analytics.questionType || 'general'
  }
}

/**
 * ✅ Utility: Check if AI used knowledge base
 */
export const usedKnowledgeBase = (aiResponse) => {
  return aiResponse?.usedDocuments || aiResponse?.usedFAQs || false
}

/**
 * ✅ Debug helper for development
 */
export const debugAI = {
  // Test different questions
  testQuestions: [
    "Hey wie geht es dir?",
    "What are your prices?", 
    "How does your product work?",
    "I want to speak to a human",
    "What features do you have?"
  ],
  
  // Test with different thresholds
  async testThresholds(question, companyId) {
    const thresholds = [30, 50, 70, 90]
    const results = {}
    
    for (const threshold of thresholds) {
      try {
        const result = await testAIWithThreshold(question, companyId, threshold)
        results[`${threshold}%`] = {
          escalated: result.requiresHumanAgent,
          confidence: result.confidence,
          reason: result.escalationReason
        }
      } catch (error) {
        results[`${threshold}%`] = { error: error.message }
      }
    }
    
    console.table(results)
    return results
  },
  
  // Log current settings
  async showSettings(companyId) {
    const preferences = await getCompanyAIPreferences(companyId)
    console.log('🎨 Current AI Settings:', preferences)
    return preferences
  }
}

// Export debug helper in development
if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
  window.debugAI = debugAI
}

export default {
  queryAI,
  getCompanyAIPreferences,
  testAIWithThreshold,
  submitAIFeedback,
  shouldEscalate,
  getEscalationReason,
  formatAIAnalytics,
  usedKnowledgeBase,
  debugAI
}